import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardCheck, FileUp, Loader2, PlayCircle, Search, Settings2, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChariotVisuel } from "@/components/depot/ChariotVisuel";
import { GestionChariots } from "@/components/depot/GestionChariots";
import { InfoFichier, correspond } from "@/components/depot/InfoFichier";
import { EcheanceBadge } from "@/components/colis/Echeance";
import { fmtDateHeure } from "@/components/scans/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { numeroLisible } from "@/lib/colisCode";
import { colisSubnav } from "@/lib/subnavs";
import { StatutColis } from "./ColisPage";
import { peut } from "@/lib/permissions";
import { useDouchette } from "@/lib/douchette";
import { lireCode } from "@/lib/colisCode";
import { CapteurLecteur } from "@/components/colis/ChampLecteur";

/** Colis > Dépôt restant : chariots du dernier inventaire validé. */
export default function DepotPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [etat, setEtat] = useState(null);
  const [enCours, setEnCours] = useState(undefined);
  const [invId, setInvId] = useState("");
  const [sel, setSel] = useState(null); // emplacement sélectionné
  const [q, setQ] = useState("");
  const [gestion, setGestion] = useState(false);
  // Lecteur / douchette : scanner un colis le cherche dans les chariots.
  useDouchette((code) => { const lu = lireCode(code); setQ(lu.type ? lu.numero : code); }, !gestion);
  const [envoi, setEnvoi] = useState(false);
  const detail = useRef(null);
  // Sur téléphone, la liste est sous les chariots : on y descend.
  const choisir = (id) => {
    setSel(id);
    if (window.innerWidth < 1024) setTimeout(() => detail.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const charger = useCallback(async () => {
    try {
      const [e, c] = await Promise.all([api.get("/depot/etat", { params: invId ? { inventaire_id: invId } : {} }), api.get("/depot/inventaire")]);
      setEtat(e.data); setEnCours(c.data);
    } catch (err) { toast.error(errMsg(err)); }
  }, [invId]);
  useEffect(() => { charger(); }, [charger]);

  // Seuls les chariots qui ont des colis sont affichés (vides après la remise à zéro de la nuit).
  const pleins = (etat?.chariots || []).filter((ch) => ch.nb > 0);
  const anciens = (etat?.historique || []).filter((h) => !(etat?.du_jour && h.id === etat.inventaire?.id) && h.id !== invId);
  const emplacements = useMemo(() => (etat?.chariots || []).flatMap((ch) => ch.emplacements.map((e) => ({ ...e, chariot: ch }))), [etat]);
  const total = emplacements.reduce((n, e) => n + e.colis.length, 0);
  // Recherche : n° de colis, ou RS expéditeur / RS destinataire (fichier du transporteur).
  const recherche = q.trim().length >= 3 ? q.trim() : "";
  const trouves = useMemo(() => (recherche
    ? emplacements.flatMap((e) => e.colis.filter((c) => correspond(c, recherche)).map((c) => ({ ...c, emp: e })))
    : []), [emplacements, recherche]);
  const trouvesHors = useMemo(() => (recherche
    ? (etat?.ecarts?.non_scannes || []).filter((x) => correspond({ numero: x.numero, info: x }, recherche))
    : []), [etat, recherche]);

  const importer = async (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    setEnvoi(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/depot/fichier", fd);
      toast.success(`Fichier importé : ${data.nb} colis (inventaire ${data.centre || ""} du ${data.date_inventaire ? data.date_inventaire.split("-").reverse().join("/") : "?"}${data.heure_inventaire ? ` à ${data.heure_inventaire}` : ""})`);
      charger();
    } catch (err) { toast.error(errMsg(err)); } finally { setEnvoi(false); }
  };
  const surligne = [...new Set(trouves.map((t) => t.emp.id))];
  const selEmp = emplacements.find((e) => e.id === sel);

  return (
    <AppShell title="Dépôt restant" subnav={colisSubnav()}>
      <CapteurLecteur onScan={(code) => { const lu = lireCode(code); setQ(lu.type ? lu.numero : code); }} actif={!gestion} />
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            Dépôt restant
            {peut(user, "depot_chariots") && (
              <button onClick={() => setGestion(true)} className="text-xs font-normal px-2 py-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent inline-flex items-center gap-1" data-testid="btn-gerer-chariots">
                <Settings2 size={12} /> Gérer les chariots
              </button>
            )}
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="depot-info">
            {!etat ? "Chargement…" : etat.inventaire
              ? <>{total} colis · inventaire validé le {fmtDateHeure(etat.inventaire.fin)} par {etat.inventaire.valide_par?.nom || "—"}</>
              : <>Pas encore d'inventaire aujourd'hui : les chariots sont remis à zéro chaque nuit à {etat.remise_a_zero || "1 h"}.</>}
          </p>
          {etat?.fichier && (
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="depot-fichier">
              Fichier transporteur : {etat.fichier.nb} colis{etat.fichier.centre ? ` · ${etat.fichier.centre}` : ""}
              {etat.fichier.date_inventaire ? ` · inventaire du ${etat.fichier.date_inventaire.split("-").reverse().join("/")}${etat.fichier.heure_inventaire ? ` ${etat.fichier.heure_inventaire}` : ""}` : ""}
              {` · importé le ${fmtDateHeure(etat.fichier.importe_le)} par ${etat.fichier.par?.nom || "—"}`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {anciens.length > 0 && (
            <select value={invId} onChange={(e) => { setInvId(e.target.value); setSel(null); }} className="h-9 rounded-md border border-input bg-transparent px-2 text-sm" data-testid="depot-historique">
              <option value="">Aujourd'hui</option>
              {anciens.map((h) => <option key={h.id} value={h.id}>Inventaire du {fmtDateHeure(h.fin)}</option>)}
            </select>
          )}
          {peut(user, "depot_fichier") && <label className="inline-flex" title="Fichier d'inventaire du transporteur (CSV) : RS expéditeur / destinataire de chaque colis">
            <input type="file" accept=".csv,.CSV,.txt,.xlsx,text/csv" className="hidden" onChange={importer} data-testid="depot-import" />
            <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-input text-sm cursor-pointer hover:bg-accent">
              {envoi ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />} Importer le fichier
            </span>
          </label>}
          {peut(user, "depot_inventaire") && <Button onClick={() => navigate("/colis/depot/inventaire")} data-testid="btn-inventaire">
            {enCours ? <PlayCircle size={15} /> : <ClipboardCheck size={15} />} {enCours ? `Reprendre l'inventaire (${enCours.nb})` : "Inventaire dépôt restant"}
          </Button>}
        </div>
      </div>

      <div className="relative sm:w-80 mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N° de colis, expéditeur ou destinataire…" className="pl-9" data-testid="depot-recherche" />
        {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground" aria-label="Effacer"><X size={14} /></button>}
      </div>
      {recherche && (
        <div className="mb-5 rounded-xl border border-border bg-card p-3" data-testid="depot-resultats">
          {trouves.length === 0 && trouvesHors.length === 0 ? <p className="text-sm text-muted-foreground">Aucun colis « {q} » au dépôt.</p> : (
            <ul className="divide-y divide-border text-sm">
              {trouves.slice(0, 50).map((t) => (
                <li key={t.numero} className="py-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-bold">{numeroLisible(t.numero)}</span>
                    <span className="text-muted-foreground">→</span>
                    <button className="font-semibold text-primary hover:underline" onClick={() => choisir(t.emp.id)}>{t.emp.nom}</button>
                    {t.fiche && <StatutColis c={t.fiche} detail />}
                  </div>
                  <InfoFichier info={t.info} compact recherche={recherche} numero={t.numero} />
                </li>
              ))}
              {trouvesHors.slice(0, 30).map((x) => (
                <li key={x.numero} className="py-1.5" data-testid="resultat-hors-chariot">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-bold">{numeroLisible(x.numero)}</span>
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400">dans le fichier, sur aucun chariot</span>
                  </div>
                  <InfoFichier info={x} compact recherche={recherche} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {etat && pleins.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="depot-vide">
          {etat.chariots.length === 0
            ? <>Aucun chariot. {peut(user, "depot_chariots") ? "Cliquez sur « Gérer les chariots » pour les créer." : "Un responsable doit d'abord créer les chariots."}</>
            : etat.inventaire
              ? "Aucun colis au dépôt d'après cet inventaire."
              : <>Aucun colis au dépôt pour l'instant.<br />Lancez l'« Inventaire dépôt restant » : les chariots apparaîtront avec leurs colis.</>}
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1fr_380px] items-start">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4" data-testid="depot-chariots">
            {pleins.map((ch) => (
              <div key={ch.id} className="flex flex-col items-center gap-1 text-foreground">
                <ChariotVisuel type={ch.type} lettre={ch.lettre} emplacements={ch.emplacements} selection={sel} surligne={surligne}
                  onSelect={(e) => choisir(e.id)} testId={`visuel-${ch.lettre}`} />
                <span className="text-xs text-muted-foreground">{ch.nb} colis</span>
              </div>
            ))}
          </div>
          <aside ref={detail} className="rounded-xl border border-border bg-card p-4 lg:sticky lg:top-4 scroll-mt-4" data-testid="depot-detail">
            {!selEmp ? <p className="text-sm text-muted-foreground">Cliquez sur un chariot (ou un étage) pour voir ses colis.</p> : (
              <>
                <p className="font-heading font-semibold">{selEmp.nom}</p>
                <p className="text-xs text-muted-foreground mb-3">{selEmp.colis.length} colis · code {selEmp.code}</p>
                {selEmp.colis.length === 0 ? <p className="text-sm text-muted-foreground">Vide.</p> : (
                  <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto">
                    {selEmp.colis.map((c) => (
                      <li key={c.numero} className={`py-2 text-sm ${correspond(c, recherche) ? "bg-red-500/10 -mx-2 px-2 rounded" : ""}`}>
                        <div className="flex items-center justify-between gap-2">
                          {c.fiche && peut(user, "colis_colis") ? <Link to={`/colis/${c.fiche.id}`} className="font-mono font-bold hover:underline">{numeroLisible(c.numero)}</Link>
                            : <span className="font-mono font-bold">{numeroLisible(c.numero)}</span>}
                          <span className="text-xs text-muted-foreground">{c.type_label}</span>
                        </div>
                        <InfoFichier info={c.info} recherche={recherche} numero={c.numero} />
                        {c.fiche && (
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <StatutColis c={c.fiche} detail />
                            {c.fiche.echeance && <EcheanceBadge e={c.fiche.echeance} court />}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </aside>
        </div>
      )}
      {etat?.orphelins?.length > 0 && (
        <p className="mt-4 text-xs text-muted-foreground">{etat.orphelins.length} colis rangés sur un chariot supprimé depuis l'inventaire : {etat.orphelins.map((c) => c.numero).join(", ")}</p>
      )}
      {peut(user, "depot_chariots") && <GestionChariots open={gestion} onClose={() => setGestion(false)} onChange={charger} />}
    </AppShell>
  );
}
